import DOMPurify from 'dompurify';

const ALLOWED_CSS_PROPERTIES = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'font-size',
  'font-family',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-decoration',
  'text-decoration-color',
  'text-decoration-style',
  'text-decoration-thickness',
  'text-transform',
  'text-shadow',
  'text-indent',
  'white-space',
  'word-break',
  'word-spacing',
  'list-style-type',
  'list-style-position',
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-color',
  'border-style',
  'border-width',
  'border-radius',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'width',
  'max-width',
  'height',
  'max-height',
  'vertical-align',
  'opacity',
]);

// Viewport-relative units enable full-screen overlays (UI redress).
// Block them on layout-affecting properties even if the property is allowed.
const VIEWPORT_UNIT_RE = /\b\d+(?:\.\d+)?(?:vw|vh|vmin|vmax|svw|svh|lvw|lvh|dvw|dvh)\b/i;
const LAYOUT_PROPERTIES = new Set([
  'width', 'max-width', 'height', 'max-height',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
]);

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  if (data.attrName !== 'style' || !data.attrValue) return;

  const filtered = data.attrValue
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const colon = decl.indexOf(':');
      if (colon === -1) return false;
      const prop = decl.slice(0, colon).trim().toLowerCase();
      const value = decl.slice(colon + 1).trim();
      if (!ALLOWED_CSS_PROPERTIES.has(prop)) return false;
      if (LAYOUT_PROPERTIES.has(prop) && VIEWPORT_UNIT_RE.test(value)) return false;
      return true;
    })
    .join('; ');

  data.attrValue = filtered;
  if (!filtered) data.keepAttr = false;
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Sanitize HTML string for safe use with dangerouslySetInnerHTML */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p', 'br', 'hr', 'b', 'i', 'em', 'strong', 'u', 's', 'small', 'sub', 'sup', 'mark',
      'a', 'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'blockquote', 'pre', 'code', 'kbd', 'samp', 'var',
      'abbr', 'cite', 'q', 'dfn', 'time',
      'details', 'summary',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      'img', 'figure', 'figcaption',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel', 'download',
      'class', 'style',
      'src', 'alt', 'title', 'width', 'height', 'loading',
      'colspan', 'rowspan', 'scope',
      'datetime',
    ],
  });
}
